import { useState } from 'react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { usePerimetre } from '../site/usePerimetre';
import {
  useApercuExport,
  urlFichierExport,
  urlClasseurExport,
  urlExportRqth,
  useSynchroniserExport,
  TOUTES_FORMATIONS,
  type ColonneExport,
} from '../lib/useExport';
import { useReferentiels, urlExportSifa } from '../lib/useDocuments';
import {
  PanneauExport,
  type FormatExport,
  type JeuDeDonnees,
} from '../components/export/PanneauExport';
import { ErreurApi } from '../lib/api';

function horodatage(valeur: string | null): string {
  if (!valeur) return 'jamais';
  return new Date(valeur).toLocaleString('fr-FR', {
    timeZone: 'Indian/Reunion',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

/**
 * Écran « Export & tableau de suivi partagé » du pôle administratif (CDC §4.7).
 *
 * Une ligne par dossier, construite depuis les données déjà saisies. Les
 * colonnes cochées valent pour l'aperçu, le fichier téléchargé et la
 * synchronisation : une seule sélection, trois usages.
 */
export function AdmExportPage(): JSX.Element {
  const { site } = usePerimetre();
  const [selection, setSelection] = useState<string[] | null>(null);
  const [formation, setFormation] = useState<string>(TOUTES_FORMATIONS);
  // Ce qu'on exporte et sous quelle forme : deux questions distinctes, posées
  // à l'écran plutôt que devinées d'un bouton.
  const [jeu, setJeu] = useState<JeuDeDonnees>('suivi');
  const [format, setFormat] = useState<FormatExport>('csv');
  const colonnes = selection ?? [];

  const { data, isLoading, error } = useApercuExport(site, colonnes, formation);
  const synchronisation = useSynchroniserExport();
  const referentiels = useReferentiels();

  const disponibles = data?.colonnesDisponibles ?? [];
  const affichees = data?.colonnes ?? [];
  const cochees = new Set(selection ?? disponibles.map((colonne) => colonne.cle));

  function basculer(colonne: ColonneExport): void {
    if (colonne.obligatoire) return;
    const base = new Set(selection ?? disponibles.map((c) => c.cle));
    if (base.has(colonne.cle)) base.delete(colonne.cle);
    else base.add(colonne.cle);
    setSelection([...base]);
  }

  const etat = data?.synchronisation;
  const formations = data?.formations ?? [];
  // Total du périmètre de sites, indépendant du filtre formation en cours :
  // `data.total` compte les lignes déjà filtrées, il ferait mentir l'option.
  const totalPerimetre = formations.reduce((somme, entree) => somme + entree.dossiers, 0);

  /** Périmètre en clair, tel qu'il sera appliqué au fichier téléchargé. */
  const perimetreLisible = [
    site && site !== 'TOUS' ? `Antenne ${site}` : 'Toutes les antennes',
    formation === TOUTES_FORMATIONS
      ? 'toutes formations'
      : (formations.find((entree) => entree.code === formation)?.titre ?? formation),
  ].join(' · ');

  /**
   * Changer de jeu de données peut rendre le format courant impossible : la
   * remontée SIFA n'existe qu'en CSV, le tableau RQTH qu'en Excel. On y bascule
   * plutôt que de laisser un choix affiché mais inapplicable.
   */
  function choisirJeu(suivant: JeuDeDonnees): void {
    setJeu(suivant);
    if (suivant === 'sifa') setFormat('csv');
    if (suivant === 'rqth') setFormat('xlsx');
  }

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Export & tableau de suivi partagé"
        sousTitre="Pôle administratif · une ligne par dossier, alimentée depuis les données déjà saisies"
        actions={
          <BoutonPrincipal
            onClick={() => synchronisation.mutate({ site, colonnes, formation })}
            disabled={synchronisation.isPending}
          >
            {synchronisation.isPending ? 'Synchronisation…' : 'Synchroniser maintenant'}
          </BoutonPrincipal>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <BarreSites />

        <label className="flex items-center gap-2 text-xs text-codialis-gray">
          <span className="whitespace-nowrap">Formation</span>
          <select
            value={formation}
            onChange={(evenement) => setFormation(evenement.target.value)}
            className="min-h-[44px] rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy"
          >
            <option value={TOUTES_FORMATIONS}>
              Toutes les formations{data ? ` (${totalPerimetre})` : ''}
            </option>
            {formations.map((entree) => (
              <option key={entree.code} value={entree.code}>
                {entree.titre} ({entree.dossiers})
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 min-w-0">
          <Carte
            titre="Aperçu des lignes exportées"
            actions={
              data && (
                <span className="text-xs text-codialis-gray">
                  {data.lignes.length} ligne{data.lignes.length > 1 ? 's' : ''} affichée
                  {data.lignes.length > 1 ? 's' : ''} sur {data.total}
                </span>
              )
            }
            sansBordure
          >
            {error && (
              <div className="p-4 sm:p-6">
                <MessageErreur
                  message={
                    error instanceof ErreurApi
                      ? error.message
                      : 'L’aperçu de l’export n’a pas pu être chargé.'
                  }
                />
              </div>
            )}

            {isLoading && <Chargement libelle="Construction de l’aperçu…" />}

            {!isLoading && !error && (
              // Un tableau large défile de côté : la zone prend le focus pour
              // défiler aux flèches (RGAA 12.8).
              <div className="relative overflow-x-auto" role="region" aria-label="Aperçu de l’export" tabIndex={0}>
                <table className="w-full min-w-[1120px] text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50">
                    <tr>
                      {affichees.map((colonne) => (
                        <th
                          key={colonne.cle}
                          className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                        >
                          {colonne.libelle}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 font-mono text-xs">
                    {(data?.lignes ?? []).length === 0 && (
                      <tr>
                        <td
                          colSpan={Math.max(1, affichees.length)}
                          className="px-4 sm:px-6 py-10 text-center font-sans text-sm text-codialis-gray"
                        >
                          {formation
                            ? 'Aucun dossier pour cette formation dans le périmètre affiché'
                            : 'Aucun dossier dans le périmètre affiché'}
                        </td>
                      </tr>
                    )}
                    {(data?.lignes ?? []).map((ligne, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        {affichees.map((colonne) => (
                          <td key={colonne.cle} className="px-4 py-2.5 text-codialis-gray">
                            {ligne[colonne.cle] || '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Carte>
        </div>

        <div className="space-y-6">
          <PanneauExport
            jeu={jeu}
            format={format}
            onJeu={choisirJeu}
            onFormat={setFormat}
            fichiers={data?.fichiers ?? null}
            perimetre={perimetreLisible}
            lignesSuivi={data?.total ?? null}
            colonnesSuivi={affichees.length}
            colonnesSifa={(referentiels.data?.colonnesSifa ?? []).length}
            rqth={data?.rqth ?? { dossiers: 0, attestationManquante: 0 }}
            onTelecharger={() => {
              window.location.href =
                jeu === 'sifa'
                  ? urlExportSifa(site, formation)
                  : jeu === 'rqth'
                    ? urlExportRqth(site, formation)
                    : format === 'xlsx'
                      ? urlClasseurExport(site, formation)
                      : urlFichierExport(site, colonnes, formation);
            }}
          />

          <Carte titre="Synchronisation" sansBordure>
            <div className="space-y-3 p-4 sm:p-6">
              {[
                ['Destination', etat?.destination ?? '—'],
                ['Dernière synchro', horodatage(etat?.derniereSynchro ?? null)],
                [
                  'Lignes de la dernière synchro',
                  etat?.lignesEnvoyees === null || etat?.lignesEnvoyees === undefined
                    ? '—'
                    : String(etat.lignesEnvoyees),
                ],
              ].map(([libelle, valeur]) => (
                <div key={libelle} className="flex justify-between gap-3 text-sm">
                  <span className="text-codialis-gray">{libelle}</span>
                  <span className="text-right font-medium text-codialis-navy">{valeur}</span>
                </div>
              ))}

              {etat && !etat.operationnelle && (
                <div className="rounded-xl bg-amber-50 p-3 text-xs text-codialis-navy">
                  <Pastille texte="Non transmis" ton="warning" />
                  <p className="mt-2">
                    Aucune destination partagée n’est retenue à ce jour. Les lignes sont
                    construites et journalisées, mais rien ne quitte la plateforme. Le
                    branchement se fera à la validation du prestataire.
                  </p>
                </div>
              )}

              {synchronisation.data && (
                <div className="rounded-xl bg-codialis-sand p-3 text-xs text-codialis-navy">
                  {synchronisation.data.nombreLignes} ligne
                  {synchronisation.data.nombreLignes > 1 ? 's' : ''} préparée
                  {synchronisation.data.nombreLignes > 1 ? 's' : ''} ·{' '}
                  {synchronisation.data.message ?? synchronisation.data.issue}
                </div>
              )}

              {synchronisation.error instanceof ErreurApi && (
                <MessageErreur message={synchronisation.error.message} />
              )}

              <p className="text-[11px] text-codialis-gray">
                Chaque dossier produit une ligne : nom, prénom, entreprise, conseiller
                référent et dates clés. Ces cinq informations ne sont pas décochables.
              </p>
            </div>
          </Carte>

          <Carte titre="Colonnes exportées" sansBordure>
            {/* Les colonnes ne valent que pour l'aperçu et le CSV du suivi. Le
                dire ici, à côté des cases, plutôt que de laisser cocher sans
                effet visible sur le fichier demandé. */}
            {(jeu !== 'suivi' || format === 'xlsx') && (
              <p className="px-4 sm:px-6 pt-4 text-[11px] text-codialis-gray">
                Sans effet sur le fichier retenu ci-dessus
                {jeu === 'sifa'
                  ? ' : l’ordre des colonnes SIFA est imposé.'
                  : jeu === 'rqth'
                    ? ' : les colonnes du tableau RQTH servent l’accompagnement, elles sont fixes.'
                    : ' : la forme du classeur Excel est celle des tableaux de TOP FORMATION.'}{' '}
                Ces cases valent pour l’aperçu et pour le CSV du tableau de suivi.
              </p>
            )}
            <div className="grid grid-cols-2 gap-2 p-4 sm:p-6">
              {disponibles.map((colonne) => (
                <label
                  key={colonne.cle}
                  className={`flex items-center gap-2 text-xs ${
                    colonne.obligatoire
                      ? 'cursor-default text-codialis-gray'
                      : 'cursor-pointer text-codialis-navy'
                  }`}
                  title={colonne.obligatoire ? 'Colonne toujours exportée' : ''}
                >
                  <input
                    type="checkbox"
                    className="rounded"
                    checked={cochees.has(colonne.cle)}
                    disabled={colonne.obligatoire}
                    onChange={() => basculer(colonne)}
                  />
                  {colonne.libelle}
                </label>
              ))}
            </div>
          </Carte>
        </div>
      </div>

      <Carte titre="Remontée SIFA · 47 colonnes" sansBordure>
        <div className="space-y-3 p-4 sm:p-6">
          <div className="flex flex-wrap gap-1">
            {(referentiels.data?.colonnesSifa ?? []).map((colonne) => (
              <span
                key={colonne}
                className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] break-all text-codialis-gray"
              >
                {colonne}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-codialis-gray">
            Colonnes du fichier de remontée annuelle, alimentées depuis le dossier
            apprenant, l’entreprise et le contrat. Le fichier suit le périmètre de
            sites affiché et la formation retenue ci-dessus ; la sélection de colonnes
            ne s’y applique pas, l’ordre des 47 colonnes est imposé.
          </p>
          {/* Un seul point de départ pour tous les téléchargements : un second
              bouton ici a longtemps fait croire à un export différent de celui
              du panneau. Il n'y renvoie plus, il y ramène. */}
          <BoutonSecondaire onClick={() => choisirJeu('sifa')}>
            Choisir la remontée SIFA dans le panneau d’export
          </BoutonSecondaire>
        </div>
      </Carte>
    </div>
  );
}
